import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const AboutPage = () => {
  const [roadmapOpen, setRoadmapOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="editorial-container py-20 lg:py-32 border-b border-border">
        <div className="editorial-narrow">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl editorial-serif leading-none mb-8">
            About
          </h1>
          <p className="text-lg text-foreground/80 leading-relaxed">
            Cultural Revolution is a home for filmmakers, artists, and cultural creators.
          </p>
        </div>
      </section>

      {/* What We Are */}
      <section className="editorial-container py-16 lg:py-24">
        <div className="editorial-narrow space-y-8 text-base text-foreground/80 leading-relaxed">
          <p>
            We exist to support work that's made with intention — films, projects, and ideas that shape culture rather than chase it.
          </p>
          
          <p>
            At its core, Cultural Revolution brings together traditional storytelling, independent production, and modern distribution into a shared ecosystem built for longevity.
          </p>
          
          <p className="text-lg text-foreground font-medium">
            This is not about content volume or trends.
          </p>
          
          <p className="text-lg text-foreground font-medium">
            It's about building bodies of work.
          </p>

          <p>
            Cultural Revolution is designed to give filmmakers and creators the space to develop projects, sustain momentum, and remain connected to the audiences who care about what they make.
          </p>

          <p>
            Each project stands on its own — but together, they form a living archive of independent culture.
          </p>
        </div>
      </section>

      {/* Roadmap - Collapsible */}
      <section className="editorial-container py-16 border-t border-border">
        <div className="editorial-narrow">
          <button
            onClick={() => setRoadmapOpen(!roadmapOpen)}
            className="w-full flex items-center justify-between group mb-8"
          >
            <h2 className="text-3xl editorial-serif">Roadmap</h2>
            {roadmapOpen ? (
              <ChevronUp size={24} className="text-muted-foreground group-hover:text-foreground" />
            ) : (
              <ChevronDown size={24} className="text-muted-foreground group-hover:text-foreground" />
            )}
          </button>

          {roadmapOpen && (
            <div className="space-y-16 animate-in fade-in duration-300">
              <div className="text-base text-foreground/80 leading-relaxed mb-12">
                <p>Cultural Revolution is unfolding in chapters, not sprints.</p>
              </div>

              {/* Chapter I */}
              <div className="border-l-2 border-primary pl-8 space-y-4">
                <div>
                  <span className="text-xs uppercase tracking-widest accent-red font-bold">Chapter I</span>
                  <h3 className="text-2xl editorial-serif mt-2">The Work</h3>
                </div>
                <p className="text-base text-foreground/80 leading-relaxed">
                  The foundation is the work itself.
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  This chapter centers on:
                </p>
                <ul className="text-sm text-foreground/70 space-y-2">
                  <li>• Feature films</li>
                  <li>• Series</li>
                  <li>• Projects driven by filmmakers and artists</li>
                  <li>• Stories that reflect lived experience and cultural moment</li>
                </ul>
                <p className="text-base text-foreground/80 leading-relaxed pt-4">
                  The focus is simple: put meaningful work in the world and let it speak first.
                </p>
              </div>

              {/* Chapter II */}
              <div className="border-l-2 border-border pl-8 space-y-4">
                <div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Chapter II</span>
                  <h3 className="text-2xl editorial-serif mt-2">Context</h3>
                </div>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Great work gains power when it's seen in conversation with other work.
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  This chapter focuses on:
                </p>
                <ul className="text-sm text-foreground/70 space-y-2">
                  <li>• Presenting projects with care and editorial clarity</li>
                  <li>• Connecting artists, films, and ideas across disciplines</li>
                  <li>• Offering audiences a sense of continuity and perspective</li>
                </ul>
                <p className="text-base text-foreground/80 leading-relaxed pt-4">
                  Cultural Revolution becomes a place to follow culture as it develops — not just consume it.
                </p>
              </div>

              {/* Chapter III */}
              <div className="border-l-2 border-border pl-8 space-y-4">
                <div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Chapter III</span>
                  <h3 className="text-2xl editorial-serif mt-2">Continuity</h3>
                </div>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Independent work often disappears between releases. This chapter exists to change that.
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  The focus here is:
                </p>
                <ul className="text-sm text-foreground/70 space-y-2">
                  <li>• Sustaining creative momentum</li>
                  <li>• Supporting long-term careers, not one-off projects</li>
                  <li>• Allowing artists to build recognizable bodies of work over time</li>
                </ul>
                <p className="text-base text-foreground/80 leading-relaxed pt-4">
                  Audiences aren't just discovering projects — they're growing alongside the creators behind them.
                </p>
              </div>

              {/* Chapter IV */}
              <div className="border-l-2 border-border pl-8 space-y-4">
                <div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Chapter IV</span>
                  <h3 className="text-2xl editorial-serif mt-2">Legacy</h3>
                </div>
                <p className="text-base text-foreground/80 leading-relaxed">
                  Over time, the goal is to build an archive that matters.
                </p>
                <p className="text-sm text-foreground/70 leading-relaxed">
                  A place where:
                </p>
                <ul className="text-sm text-foreground/70 space-y-2">
                  <li>• Films and projects live beyond their initial release</li>
                  <li>• Creators are represented by the totality of their work</li>
                  <li>• Independent culture is preserved, not lost to platforms or cycles</li>
                </ul>
                <p className="text-base text-foreground/80 leading-relaxed pt-4">
                  This isn't about scale for its own sake. It's about cultural permanence.
                </p>
              </div>

              {/* Where This Leads */}
              <div className="pt-8 space-y-6">
                <h3 className="text-2xl editorial-serif">Where This Leads</h3>
                <p className="text-base text-foreground/80 leading-relaxed">
                  At its best, Cultural Revolution becomes a trusted destination — a place audiences return to because they know the work has been chosen with care, and the creators behind it are supported beyond a single moment.
                </p>
                <p className="text-lg text-foreground font-medium">
                  Culture doesn't happen all at once.
                </p>
                <p className="text-lg text-foreground font-medium">
                  It's built over time.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Closing */}
      <section className="editorial-container py-16 lg:py-24 border-t border-border">
        <div className="editorial-narrow text-center">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            The system beneath independent culture.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
